import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { Flex, SimpleGrid, Textarea, Text } from "@chakra-ui/react";
import ErrorMessage from "src/components/ErrorMessage";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import DateSelectController from '@/components/fields/DateSelect';
import { BiTransfer } from "react-icons/bi";
import {
    Select as ReactSelect
} from "chakra-react-select";
import { AmountInputController, CustomAmountInput } from '@/components/fields/AmountInput';
import { LabelAndValueOption } from "src/data/LabelAndValueOption";
import { useProjectNames } from '@/hooks/useProjectNames';
import { partnerService } from "src/service/PartnerService";
import CurrencySelectController from "@/components/fields/CurrencySelect";
import { TransferMove } from "src/api/TransferMove";
import { transferMoveService } from "src/service/TransferMoveService";
import { AmountUtil } from "src/data/AmountUtil";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { Field } from "@/components/ui/field";
import { InputGroup } from "@/components/ui/input-group";
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface TransfersMoveFormProps {
    transferMoveId?: number;
    organizationId: number;
    onSubmit: (ps: TransferMove) => Promise<void>;
    viewOnly?: boolean;
    header: string;
}

const TransfersMoveForm = ({ transferMoveId, organizationId, onSubmit, viewOnly, header }: TransfersMoveFormProps): JSX.Element => {
    const { t } = useI18n();
    const organization = useOrganization();
    const homeCurrency = organization?.currency;
    const { control, register, handleSubmit, reset, setValue, getValues, formState: { isSubmitting }, watch } = useForm<TransferMove>({
        defaultValues: {
            id: transferMoveId,
            transferDate: new Date(),
            partnerId: -1,
            fromProjectId: -1,
            toProjectId: -1,
            localTotal: 0,
            currency: '',
            rate: 1,
            total: 0,
            comments: '',
            organizationId: organizationId
        }
    });
    useEffect(() => {
        if (!(transferMoveId > -1) && homeCurrency && !getValues('currency')) {
            setValue('currency', homeCurrency);
        }
    }, [transferMoveId, homeCurrency, getValues, setValue]);
    const [serverError, setServerError] = useState('');
    const { options: projectNames } = useProjectNames();
    const [partnerNames, setPartnerNames] = useState<LabelAndValueOption[]>([]); // unique names for institutes
    const watchLocalTotal = watch('localTotal', 0);
    const watchRate = watch('rate', 1);
    const watchCurrency = watch('currency');
    const sameCurrencyEntry = !(transferMoveId > -1) && watchCurrency === homeCurrency;
    const navigate = useNavigate();
    useEffect(() => {
        if (!(transferMoveId > -1) && homeCurrency && watchCurrency === homeCurrency) setValue('rate', 1);
    }, [transferMoveId, homeCurrency, watchCurrency, setValue]);

    useEffect(() => {
        if (transferMoveId > -1) {
            transferMoveService.find(transferMoveId).then(p => reset(p));
        }
    }, [transferMoveId, reset]);

    useEffect(() => {
        let b = partnerService.listAll().then(ns => {
            let ops = ns.map(n => {
                let o: LabelAndValueOption = {
                    label: n.name,
                    value: n.id
                }
                return o;
            });
            setPartnerNames(ops);
        });
        Promise.all([b]);
    }, []);

    const readOnly = (viewOnly ?? false) || !homeCurrency;
    console.info(`form is readonly: ${readOnly}`);

    const onSubmitInternal = (t: TransferMove): Promise<void> => {
        if (!homeCurrency || !Number.isFinite(t.rate) || t.rate <= 0) return Promise.resolve();
        let c: TransferMove = {
            ...t,
            total: t.localTotal / t.rate
        };
        console.info(`submitting ${JSON.stringify(c)}`);
        return onSubmit(c)
            .then(() => navigate('/transfers/moves'))
            .catch(_err => setServerError('Fehler'));
    };

    return (
        <FormPage title={header} description={t('forms.partnerCarryovers.description')}
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate('/transfers/moves/')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError && <ErrorMessage message={serverError} />}
            <SectionPanel title={t('forms.sections.assignment')}>
                <Controller control={control} name='partnerId'
                    rules={{ required: "Partner auswählen", validate: (value) => value > -1 || 'Partner auswählen' }}
                    render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
                        <Field id="partnerId" label='Partner' required disabled={readOnly} invalid={invalid} errorText={error?.message}>
                            <ReactSelect
                                isSearchable
                                required
                                isDisabled={readOnly}
                                autoFocus={true}
                                ref={ref}
                                name={name}
                                inputId='partnerId'
                                onBlur={onBlur}
                                placeholder='Partner'
                                options={partnerNames}
                                onChange={val => onChange(val.value)}
                                value={partnerNames.find(c => c.value === value)}
                            />
                        </Field>
                    )} />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <ProjectSelectController control={control} projectNames={projectNames} name='fromProjectId' label='Von Projekt' readOnly={readOnly} required/>

                    <ProjectSelectController control={control} projectNames={projectNames} name='toProjectId' label='Auf Projekt' readOnly={readOnly} required/>
                </SimpleGrid>
            </SectionPanel>

            <SectionPanel title={t('forms.sections.amountsAndRate')}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <AmountInputController control={control} name='localTotal' readOnly={readOnly} />

                    <CurrencySelectController control={control} name='currency' readOnly={readOnly} />
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <DateSelectController required disabled={readOnly} control={control} label='Datum' name='transferDate' clearable={false} />

                    <Controller control={control} name='rate'
                        rules={{ required: "Kurs eingeben", validate: (value) => Number.isFinite(value) && value > 0 && (!sameCurrencyEntry || value === 1) || "Kurs eingeben" }}
                        render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => {
                            return (
                                <Field id="rate" label={`Kurs (${homeCurrency ?? '—'}/${watchCurrency})`} required invalid={invalid} errorText={error?.message} disabled={readOnly}>
                                    <InputGroup startElement={<BiTransfer color='blue' />}>
                                        <CustomAmountInput value={value} onChange={v => onChange(sameCurrencyEntry ? 1 : v)} onBlur={onBlur} ref={ref} />
                                    </InputGroup>
                                </Field>
                            )
                        }} />
                </SimpleGrid>
                <Flex justify='flex-end' gap='3' wrap='wrap' borderTopWidth='1px' borderColor='border' pt='4'>
                    <Text>Summe:</Text>
                    <Text textAlign='right' fontWeight='bold'>{Number.isFinite(watchRate) && watchRate > 0 ? AmountUtil.format(watchLocalTotal / watchRate) : "—"}</Text>
                    <Text>{homeCurrency ?? '-'}</Text>
                </Flex>

                <Field id="comments" label='Bemerkungen' disabled={readOnly}>
                    <Textarea {...register('comments')} minH='7rem' resize='vertical' />
                </Field>

            </SectionPanel>
        </FormPage>
    );
}

export default TransfersMoveForm
